import { Badge, Box } from '@chakra-ui/react';
import { ButtonGroup, Chip, Skeleton } from '@mui/material';
import Card from 'components/card';
import MButton from 'components/Mbutton';
import MenuDrop from 'components/MenuDrop';
import MuiDatePicker from 'components/MuiDatePicker';
import MuiSelect from 'components/MuiSelect';
import MuiTable from 'components/MuiTable';
import PaginateBox from 'components/PaginateBox';
import SearchModal from 'components/SearchModal';
import CheckAuthorization from 'components/security/CheckAutorization';
import { FromArchiveIcon, FromTheFieldIcon } from 'components/TableIcon';
import { CHECK_PERMISSIONS, ENTERPRISE_HEADER, ROLES_LIST } from 'Constaints';
import useDataFetching from 'hooks/useDataFetching';
import useAppQuery from 'hooks/useAppQuery';
import { useEffect, useMemo, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import { useTranslation } from 'react-i18next';
import {
  BsGeoAlt,
  BsList,
  BsMinecart,
  BsPersonFill,
  BsPlusCircle,
  BsSearch,
  BsThreeDotsVertical,
  BsXCircle,
} from 'react-icons/bs';
import { useLocation, useNavigate } from 'react-router-dom';
import { castToRequestParams, createMapLink, formatDate, formatNumber } from 'utils';
import { config } from 'variables/config';
import PrintButtonExcel from '../default/components/PrintButtonExcel';
import EmployesByEnterpriseModal from '../employes/EmployesByEnterpriseModal';
import AddEnterprise from './AddEnterprise';
import CheckPrivileges from 'components/CheckPrivileges';
import toast from 'react-hot-toast';
import GeoRestrictionAlert from 'components/GeoRestrictionAlert';

const Enterprises = () => {
  const { t } = useTranslation('enterprises');
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const handleClose = () => {
    setSelectedEnterprise(null);
    setOpen(false);
  };
  const navigation = useNavigate();

  // Traduire les labels de ENTERPRISE_HEADER
  const translatedHeaders = ENTERPRISE_HEADER.map((header) => ({
    ...header,
    label: t(header.label),
  }));

  const [searchTerm, setSearchTerm] = useState(null);
  // const defaultText = '😕 Aucune entreprise trouvée avec ces paramètres.';
  // const [noDataFoundText, setNoDataFoundText] = useState(defaultText);
  const defaultText = '😕 Aucune entreprise trouvée avec ces paramètres.';
  const [noDataFoundText, setNoDataFoundText] = useState(defaultText);

  /* Regions — static reference data, cached 5 min, survives navigation */
  const { isLoading: loadingR, data: regions } = useAppQuery(`${config.app.api_url}/regions`);

  /* Missions — cached 5 min */
  const { data: missions } = useAppQuery(`${config.app.api_url}/missions`);

  const [loadingSearch, , searchEnterprises, fetchDataSearch] = useDataFetching(
    searchTerm?.length > 2
      ? `${config.app.api_url}/enterprises/search-with-deleted/${searchTerm}`
      : null
  );

  const [region, setRegion] = useState('*');
  const [departement, setDepartement] = useState(null);

  useEffect(() => {
    setRegion(location?.state?.regionId);
  }, [location]);

  const [, setArrondissement] = useState(null);

  useEffect(() => {
    setDepartement(location?.state?.departmentId);
  }, [location]);

  /* Departments by region — cached per region id */
  const { data: departements } = useAppQuery(
    region && region !== '*' ? `${config.app.api_url}/regions/${region}/departments` : null
  );

  /* Arrondissements by department — cached per department id */
  const { data: arrondisements } = useAppQuery(
    departement ? `${config.app.api_url}/departments/${departement}/arrondissements` : null
  );

  /* Zones — cached per region (or all zones) */
  const { data: zones } = useAppQuery(
    region && region != '*'
      ? `${config.app.api_url}/regions/${region}/zones`
      : `${config.app.api_url}/zones`
  );

  const auth = useAuthUser();
  /* Current user — cached 5 min */
  const { data: user } = useAppQuery(`${config.app.api_url}/users/${auth()?.user?.id}`);

  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [openList, setOpenList] = useState(false);

  const [params, setParams] = useState({
    enterpriseId: location.state?.enterpriseId,
    start: location.state?.start,
    end: location.state?.end,
    startUpdate: location.state?.startUpdate,
    endUpdate: location.state?.endUpdate,
    isRegular: location.state?.isRegular,
    regionId: location.state?.regionId,
    departmentId: location.state?.departmentId,
    arrondissementId: location.state?.arrondissementId,
    page: 0,
    limit: 50,
    fromArchive: location.state?.fromArchive,
    located: location.state?.located,
    creatorId: location.state?.creator?.id,
    zoneId: location.state?.zone?.id,
    missionId: location.state?.mission?.id,
    taskId: location.state?.task?.id,
  });

  const [creator, setCreator] = useState(null);
  const [mission, setMission] = useState(null);
  const [zone, setZone] = useState(null);
  const [task, setTask] = useState(null);
  const [hideOptions, setHideOptions] = useState(false);

  useEffect(() => {
    if (creator || mission || zone || task) {
      setHideOptions(true);
    }
  }, [creator, mission, zone, task]);

  useEffect(() => {
    if (location.state) {
      setCreator(location.state?.creator);
      setMission(location.state?.mission);
      setZone(location.state?.zone);
      setTask(location.state?.task);
      setParams({
        enterpriseId: location.state?.enterpriseId,
        start: location.state?.start,
        end: location.state?.end,
        startUpdate: location.state?.startUpdate,
        endUpdate: location.state?.endUpdate,
        isRegular: location.state?.isRegular,
        regionId: location.state?.regionId,
        departmentId: location.state?.departmentId,
        arrondissementId: location.state?.arrondissementId,
        page: 0,
        limit: 50,
        fromArchive: location.state?.fromArchive,
        located: location.state?.located,
        creatorId: location.state?.creator?.id,
        zoneId: location.state?.zone?.id,
        missionId: location.state?.mission?.id,
        taskId: location.state?.task?.id,
      });
    }
  }, [location]);

  // Build a stable query string from params — becomes part of the React Query cache key.
  // Same params on re-navigation → cache hit → NO network call.
  const enterprisesQueryString = useMemo(() => {
    return Object.keys(params)
      .filter((key) => params[key] != null && params[key] !== '')
      .map((key) => `${key}=${params[key]}`)
      .join('&');
  }, [params]);

  const {
    isLoading: loading,
    data: enterprisesRaw,
    error: enterprisesError,
    refetch: get,
  } = useAppQuery(
    `${config.app.api_url}/enterprises/query-names-only?${enterprisesQueryString}`,
    { staleTime: 30_000 } // paginated filtered list — refresh after 30 s
  );

  // The API returns { data: { content, totalElements, totalPages } }
  // useAppQuery unwraps response.data, so we get { data: { content, ... } }
  const enterprises = enterprisesRaw?.data;

  // Handle geo-restriction errors and reset the empty-text message
  useEffect(() => {
    setNoDataFoundText(defaultText);
    if (!enterprisesError) return;

    if (
      enterprisesError?.response?.status === 403 &&
      enterprisesError?.response?.data?.isGeoRestrictionFlagged
    ) {
      const errorData = enterprisesError.response.data;
      const scopeType = errorData?.scopeType || 'périmètre géographique';
      const allowedIds = errorData?.allowedIds || '[]';
      const requestedIds = errorData?.requestedIds || '[]';

      setNoDataFoundText(
        <GeoRestrictionAlert
          scopeType={scopeType}
          allowedIds={allowedIds}
          requestedIds={requestedIds}
        />
      );

      toast.error(
        <div className="flex flex-col gap-2">
          <strong>{t('error_geo_restriction_title') || 'Restriction Géographique'}</strong>
          <p>
            {t('error_geo_restriction_message') ||
              `Vous avez tenté d'accéder à ${scopeType} en dehors de votre portée autorisée.`}
          </p>
          <div className="text-xs">
            <p>
              <strong>{t('allowed') || 'Autorisé'}:</strong> {allowedIds}
            </p>
            <p>
              <strong>{t('requested') || 'Demandé'}:</strong> {requestedIds}
            </p>
          </div>
        </div>,
        { duration: 6000, className: 'bg-red-600 text-white' }
      );
    }
  }, [enterprisesError]);
  const HeaderTable = [
    '#',
    t('header_social_reason'),
    t('header_niu'),
    t('header_sigle'),
    t('header_activity_sector'),
    t('header_employees_number'),
    t('header_arrondissement'),
    t('header_street'),
    t('header_contacts'),
  ];

  const exportPdf = (
    <CheckPrivileges roles={['ADMIN', 'QUALITY']}>
      <PrintButtonExcel
        text={t('menu_export')}
        filename={
          'Entreprises_' +
          regions?.data?.find((r) => r?.id == params?.regionId)?.name +
          `_page_${params?.page}SUR${enterprises?.totalPages}`
        }
        header={HeaderTable}
        body={enterprises?.content?.map((e, index) => {
          return [
            index + 1,
            e?.socialRaison?.toUpperCase(),
            e?.nui?.toUpperCase(),
            e?.sigle?.toUpperCase(),
            e?.activity?.toUpperCase(),
            e?.region?.toUpperCase(),
            e?.arrondissement?.toUpperCase(),
          ];
        })}
      />
    </CheckPrivileges>
  );

  const menu = {
    icon: <BsThreeDotsVertical />,
    label: t('menu_options'),
    Items: [
      {
        label: t('menu_add_enterprise'),
        icon: <BsPlusCircle />,
        handleClick: () => setOpen(true),
      },
      {
        label: null,
        icon: exportPdf,
      },
      {
        label: t('menu_reset_filter'),
        icon: <BsXCircle />,
        handleClick: () => alert('building'),
      },
    ],
  };

  const handleClick = (item) => {
    if (item) {
      setSelectedEnterprise(item);
      setOpen(true);
    }
  };

  function createData(N, Raison, Secteur, Arrondissement, Expatriés, Coordonnées, Options) {
    return { N, Raison, Secteur, Arrondissement, Expatriés, Coordonnées, Options };
  }

  const rows = !enterprises?.content
    ? []
    : enterprises?.content?.map((item, index) => {
        return {
          handleClick: () => handleClick(item),
          datas: createData(
            params?.page * params?.limit + (index + 1),
            <>
              {item?.fromArchive ? <FromArchiveIcon /> : <FromTheFieldIcon />}
              <span className="fs-c">{item?.socialRaison}</span>
            </>,
            item?.activity,
            item?.arrondissement,
            item?.employeesNumber != null ? item?.employeesNumber : '--',
            user?.data?.roles.length > 0 &&
              user?.data?.roles?.map((role) =>
                CHECK_PERMISSIONS(role?.permissions, 'Voir une entreprise sur google map') ? (
                  <>
                    {item?.latitude && item?.longitude ? (
                      <Chip
                        icon={<BsGeoAlt />}
                        color="primary"
                        className="capitalize-none"
                        variant="outlined"
                        label={t('action_view_on_map')}
                        onClick={(e) => {
                          e.preventDefault();
                          e?.stopPropagation();
                          if (item?.latitude && item?.longitude) {
                            window.open(
                              createMapLink(`${item?.latitude},${item?.longitude}`),
                              '_blank',
                              'noopener,noreferrer'
                            );
                          } else {
                            alert(t('error_incomplete_coordinates'));
                          }
                        }}
                      />
                    ) : item?.searchLatitude && item?.searchLongitude ? (
                      <Chip
                        icon={<BsGeoAlt />}
                        color="warning"
                        className="capitalize-none"
                        variant="outlined"
                        label={t('action_verify_on_map')}
                        onClick={(e) => {
                          e.preventDefault();
                          e?.stopPropagation();
                          if (item?.searchLatitude && item?.searchLongitude) {
                            window.open(
                              createMapLink(`${item?.searchLatitude},${item?.searchLongitude}`),
                              '_blank',
                              'noopener,noreferrer'
                            );
                          } else {
                            alert(t('error_incomplete_coordinates'));
                          }
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<BsSearch />}
                        className="capitalize-none"
                        label={t('action_search_on_map')}
                        onClick={(e) => {
                          e.preventDefault();
                          e?.stopPropagation();
                          window.open(
                            createMapLink(
                              `${item?.socialRaison} ${item?.sigle} ${item?.street} CAMEROON`
                            ),
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                      />
                    )}
                  </>
                ) : (
                  <></>
                )
              ),
            <MButton
              variant={'white'}
              onClick={(e) => {
                setSelectedEnterprise(item);
                e.preventDefault();
                e?.stopPropagation();
                setOpenList(true);
              }}
            >
              <BsList />
              {t('action_view_expatriates')}
            </MButton>
          ),
        };
      });

  return (
    <div>
      <div className="grid h-full grid-cols-1 gap-5">
        {loadingR ? (
          <Skeleton variant="rectangular" width={'100%'} height={90} />
        ) : (
          <Box
            bg={'rgba(255, 255, 255, 1)'}
            boxShadow="lg"
            p={2}
            rounded={'md'}
            border="1px"
            borderColor="gray.200"
            className="flex flex-wrap gap-1 items-end justify-end"
          >
            <MuiDatePicker
              label={t('filter_start_period')}
              value={params?.start}
              onChange={(newValue) => {
                setParams({
                  ...params,
                  start: new Date(newValue)?.getTime(),
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideLeft'}
            />
            <MuiDatePicker
              value={params?.end}
              label={t('filter_end_period')}
              onChange={(newValue) => {
                setParams({
                  ...params,
                  end: new Date(newValue)?.getTime(),
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideLeft'}
            />
            <MuiDatePicker
              label={t('filter_start_update')}
              value={params?.startUpdate}
              onChange={(newValue) => {
                setParams({
                  ...params,
                  startUpdate: new Date(newValue)?.getTime(),
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideLeft'}
            />
            <MuiDatePicker
              value={params?.endUpdate}
              label={t('filter_end_update')}
              onChange={(newValue) => {
                setParams({
                  ...params,
                  endUpdate: new Date(newValue)?.getTime(),
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideLeft'}
            />
            <MuiSelect
              label={t('filter_region')}
              options={regions?.data}
              emptyLabel={t('option_national')}
              value={params?.regionId}
              defaultFormat={true}
              onChange={(e) => {
                setRegion(e.target.value);
                setArrondissement('');
                setDepartement('');
                setParams({
                  ...params,
                  regionId: e.target.value,
                  departmentId: null,
                  arrondissementId: null,
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideLeft'}
            />
            {departements?.data && (
              <MuiSelect
                label={t('filter_department')}
                options={departements?.data}
                emptyLabel={t('option_regional')}
                value={params?.departmentId}
                defaultFormat={true}
                onChange={(e) => {
                  setDepartement(e.target.value);
                  setArrondissement('');
                  setParams({
                    ...params,
                    departmentId: e.target.value,
                    arrondissementId: null,
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideDown'}
              />
            )}
            {zones?.data && (
              <MuiSelect
                label={t('filter_zone')}
                value={params.zoneId}
                options={zones?.data?.map((z) => ({
                  value: z?.id,
                  label: `Zone ${z?.number} ${z?.description ? '--' + z?.description : ''}`,
                }))}
                onChange={(e) => {
                  setArrondissement(e.target.value);
                  setParams({
                    ...params,
                    zoneId: e.target.value,
                  });
                }}
                emptyLabel={t('option_none')}
              />
            )}
            {arrondisements?.data && (
              <MuiSelect
                label={t('filter_arrondissement')}
                emptyLabel={t('option_departmental')}
                options={arrondisements?.data}
                value={params?.arrondissementId}
                defaultFormat={true}
                onChange={(e) => {
                  setArrondissement(e.target.value);
                  setParams({
                    ...params,
                    arrondissementId: e.target.value,
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideDown'}
              />
            )}
            {missions?.data && (
              <MuiSelect
                label={t('filter_mission')}
                value={params.missionId}
                options={[
                  { value: '-1', label: t('option_no_control_mission') },
                  { value: '-2', label: t('option_completed_control_mission') },
                  ...missions?.data?.map((m) => ({
                    value: m?.id,
                    label: `${m?.description} du ${formatDate(m?.start)} ${formatDate(m?.end)}`,
                  })),
                ]}
                onChange={(e) => {
                  setArrondissement(e.target.value);
                  setParams({
                    ...params,
                    missionId: e.target.value,
                  });
                }}
                emptyLabel={t('option_none')}
              />
            )}
            <MuiSelect
              label={t('filter_is_regular')}
              emptyLabel={t('option_all')}
              options={[
                { value: false, label: t('option_no') },
                { value: true, label: t('option_yes') },
              ]}
              value={params?.isRegular}
              onChange={(value) => {
                alert(t('error_functionality_under_maintenance'));
                setParams({
                  ...params,
                  isRegular: null,
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideRight'}
            />
            <MuiSelect
              label={t('filter_source')}
              emptyLabel={t('option_all')}
              value={params?.fromArchive}
              options={[
                { value: false, label: t('option_field') },
                { value: true, label: t('option_archive') },
              ]}
              onChange={(value) => {
                setParams({
                  ...params,
                  fromArchive: value.target.value,
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideRight'}
            />
            <MuiSelect
              label={t('filter_located')}
              emptyLabel={t('option_all')}
              options={[
                { value: false, label: t('option_no') },
                { value: true, label: t('option_yes') },
              ]}
              value={params?.located}
              onChange={(value) => {
                setParams({
                  ...params,
                  located: value.target.value != '-1' ? value.target.value : null,
                });
              }}
              wrapperClasses={'flex-1 filter-item anime slideRight'}
            />
            <div className="anime slideRight">
              <MenuDrop menu={menu} />
            </div>
          </Box>
        )}
        {creator && (
          <Box
            bg={'yellow.100'}
            boxShadow="lg"
            p={2}
            rounded={'md'}
            border="1px"
            borderColor="gray.200"
            className="flex flex-wrap gap-1 items-end justify-center"
          >
            <span className="flex gap-2">
              <BsPersonFill size={20} className="text-blue-400" />
              {t('context_collected_by')}
              <strong>
                {creator?.creator?.firstName} {creator?.lastName}
              </strong>
            </span>
          </Box>
        )}
        {mission && (
          <Box
            bg={'yellow.100'}
            boxShadow="lg"
            p={2}
            rounded={'md'}
            border="1px"
            borderColor="gray.200"
            className="flex flex-wrap gap-1 items-end justify-center"
          >
            <span className="flex gap-2">
              {t('context_collected_during_mission')}
              <strong>
                {` ${mission?.description} du : ${formatDate(mission?.start)} au: ${formatDate(mission?.end)}`}
              </strong>
            </span>
          </Box>
        )}
        {zone && (
          <Box
            bg={'yellow.100'}
            boxShadow="lg"
            p={2}
            rounded={'md'}
            border="1px"
            borderColor="gray.200"
            className="flex flex-wrap gap-1 items-end justify-center"
          >
            <span className="flex gap-2">
              <BsPersonFill size={20} className="text-blue-400" />
              {t('context_collected_in_zone')}
              <strong>{` Zone ${zone?.number} -- ${zone?.description}`}</strong>
            </span>
          </Box>
        )}
        {task && (
          <Box
            bg={'yellow.100'}
            boxShadow="lg"
            p={2}
            rounded={'md'}
            border="1px"
            borderColor="gray.200"
            className="flex flex-wrap gap-1 items-end justify-center"
          >
            <span className="flex flex-col gap-0">
              <span className="flex">
                <BsPersonFill size={20} className="text-blue-400" />
                {t('context_assigned_to_task')}
                <strong>{task?.description}</strong>
              </span>
              <span className="w-full flex flex-wrap gap-1">
                {task?.participants?.map((p) => (
                  <Chip
                    key={p.id}
                    color="info"
                    variant="outlined"
                    label={`${p?.firstName} ${p?.lastName}`}
                  />
                ))}
              </span>
            </span>
          </Box>
        )}
        <Card extra={'w-full h-full pb-4'}>
          <div className="flex justify-between">
            <div>
              <strong>
                {t('label_total')}
                <Badge
                  fontSize={13}
                  rounded={5}
                  padding={1}
                  paddingInline={2}
                  colorScheme="green"
                  className="m-0 ms-2"
                >
                  {formatNumber(enterprises?.totalElements) || '--'}
                </Badge>
              </strong>
            </div>
            <div className="flex flex-1 justify-center">
              <span style={{ marginTop: -15 }}>
                <ButtonGroup variant="text" aria-label="Basic button group">
                  {!hideOptions && (
                    <>
                      <MButton
                        variant={params?.creatorId === auth()?.user?.id ? 'primary' : 'white'}
                        onClick={() =>
                          setParams({
                            ...params,
                            creatorId: params?.creatorId ? null : auth()?.user?.id,
                          })
                        }
                      >
                        <BsMinecart />
                        {t('my_work')}
                      </MButton>
                      <SearchModal
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        searchResults={searchEnterprises?.data}
                        searchFunction={fetchDataSearch}
                        iconLabel={t('action_search')}
                        placeholder={t('search_modal_placeholder')}
                        title={t('search_modal_title')}
                        handleClickResult={(item) => {
                          setSelectedEnterprise(item);
                          setOpen(true);
                        }}
                        loading={loadingSearch}
                      />
                      <CheckAuthorization
                        roles={[
                          ROLES_LIST.ADMIN,
                          ROLES_LIST.AGENT,
                          ROLES_LIST.QUALITY,
                          ROLES_LIST.DRMO,
                        ]}
                      >
                        <MButton variant={'white'} onClick={() => setOpen(true)}>
                          <BsPlusCircle />
                          {t('action_add')}
                        </MButton>
                      </CheckAuthorization>
                    </>
                  )}
                  <CheckAuthorization
                    roles={[
                      ROLES_LIST.ADMIN,
                      ROLES_LIST.AGENT,
                      ROLES_LIST.QUALITY,
                      ROLES_LIST.DRMO,
                    ]}
                  >
                    {exportPdf}
                  </CheckAuthorization>
                </ButtonGroup>
              </span>
            </div>
          </div>
          <MuiTable
            loading={loading}
            columns={translatedHeaders}
            limit={params?.limit}
            hidePagiation={true}
            rows={rows}
            textEmpty={noDataFoundText}
          />
          <PaginateBox
            total={enterprises?.totalElements}
            rowsPerPage={params?.limit}
            handleChangeRowsPerPage={(event) => {
              setParams({
                ...params,
                limit: parseInt(event.target.value),
                page: 0,
              });
            }}
            page={params.page}
            count={enterprises?.totalPages - 1}
            handleChange={(event, value) => {
              setParams({ ...params, page: value });
            }}
          />
        </Card>
      </div>
      {open && (
        <AddEnterprise
          get={get}
          open={open}
          handleClose={handleClose}
          selectedEnterpriseP={selectedEnterprise}
        />
      )}
      {openList && (
        <EmployesByEnterpriseModal
          Fullscreen={true}
          title={t('modal_title_1')}
          open={openList}
          setOpen={setOpenList}
          handleClose={() => {
            setOpenList(false);
            setSelectedEnterprise(null);
          }}
          selectedEnterprise={selectedEnterprise}
        />
      )}
    </div>
  );
};

export default Enterprises;
